import { useMemo, useState, type ReactNode } from 'react';
import { useCaptured } from '../capture/CaptureProvider.js';
import Board from '../components/Board.js';
import JobTabs, { groupByProject, projectTree } from '../components/JobTabs.js';
import { listProjects, listTasks } from '../lifeos/api.js';
import { addDays, dayLabel, plural, ymdInHomeZone } from '../lifeos/format.js';
import { sphereColor, useSpheres } from '../lifeos/spheres.js';
import TaskEditSheet from '../lifeos/TaskEditSheet.js';
import { SortSheet, TaskActionsSheet, TaskItem, useTaskActions } from '../lifeos/tasks.js';
import { Loader, Segmented } from '../lifeos/ui.js';
import { useLoad } from '../lifeos/useLoad.js';
import type { Project, TaskRow, TaskView } from '../lifeos/types.js';
import { Chip, EmptyState, Group, Icon, MoreRow, Page, useParamSetter, type IconName } from '../ui/index.js';

export type TasksTab = 'today' | 'upcoming' | 'someday' | 'done' | 'to_sort';

/**
 * Four views, Things-style (spec 2026-10-09 §6), plus To sort only while it
 * has something in it. `server` is the `GET /api/tasks?view=` each one reads.
 * Status `inbox` is always called "To sort" here; "Inbox" means messages.
 */
export const TASK_TABS: readonly { value: TasksTab; label: string; server: TaskView; empty: string; hint?: string; icon: IconName }[] = [
  { value: 'today', label: 'Today', server: 'now', empty: 'All clear for now.', hint: 'Snoozed and scheduled tasks come back on their own.', icon: 'sun' },
  { value: 'upcoming', label: 'Upcoming', server: 'upcoming', empty: 'Nothing scheduled.', icon: 'calendar' },
  { value: 'someday', label: 'Someday', server: 'someday', empty: 'Someday is empty.', hint: 'Ideas you might do live here, out of the way.', icon: 'archive' },
  { value: 'done', label: 'Done', server: 'done_today', empty: 'Nothing done yet today.', icon: 'check' },
  { value: 'to_sort', label: 'To sort', server: 'to_sort', empty: 'Nothing to sort.', hint: 'Captures without a date or project land here.', icon: 'sort' },
];

/** Old `?view=` values (Home links, Signal, bookmarks) still land somewhere sensible. */
const LEGACY: Record<string, TasksTab> = { now: 'today', waiting: 'today', overdue: 'today', done_today: 'done' };

export function tabFromParam(v: string | null): TasksTab {
  if (!v) return 'today';
  if (TASK_TABS.some((t) => t.value === v)) return v as TasksTab;
  return LEGACY[v] ?? 'today';
}

/** Today shows this many ranked rows; the rest are one tap away. */
export const TODAY_SHOWN = 8;

/** Day header for Upcoming: 'Tomorrow', or 'Mon Oct 12'. */
export function dayHeader(ymd: string, today: string): string {
  if (ymd === addDays(today, 1)) return 'Tomorrow';
  if (ymd === today) return 'Today';
  return dayLabel(ymd);
}

/** Board (default) or the older lists; any `?view=`/`?q=` link means the lists. */
export function modeFromParams(params: URLSearchParams): 'board' | 'list' {
  if (params.get('mode') === 'list' || params.get('view') || params.get('q')) return 'list';
  return 'board';
}

/**
 * Action items (`/tasks`, spec 2026-10-14-workspaces): job tabs, a project
 * filter and the focus board (This week · Next · Later · Waiting · Done).
 * The older Things-style lists (Today · Upcoming · Someday · Done · To sort)
 * stay one tap away under "List", so nothing is lost. Filters live in the URL.
 */
export default function Tasks(): JSX.Element {
  const [params, setParam] = useParamSetter();
  const mode = modeFromParams(params);
  const sphere = params.get('sphere');
  const project = params.get('project');
  const spheres = useSpheres();
  const toSort = useLoad(() => listTasks({ view: 'to_sort', sphere: sphere ?? undefined, limit: 1 }), [sphere]);
  // Only for the filter and group headers: a failed fetch just means fewer choices.
  const projectList = useLoad(() => listProjects().catch((): Project[] => []), []);
  const projects = projectList.data ?? [];
  const inJob = projectTree(projects.filter((p) => p.status !== 'archived' && p.status !== 'done' && (!sphere || p.sphereKey === sphere)));
  const picked = project ? projects.find((p) => p.id === project) : undefined;
  const [boardKey, setBoardKey] = useState(0);
  useCaptured(() => {
    void toSort.reload();
    setBoardKey((k) => k + 1);
  });
  const sortCount = toSort.data?.total ?? 0;

  const setMode = (m: 'board' | 'list'): void => {
    setParam('mode', m === 'list' ? 'list' : null);
    if (m === 'board') {
      setParam('view', null);
      setParam('q', null);
    }
  };

  return (
    <Page title="Action items" wide={mode === 'board'} className={`tasks${mode === 'board' ? ' tasks--board' : ''}`} sub={mode === 'list' ? <ListSub sphere={sphere} /> : undefined}>
      <JobTabs
        spheres={spheres}
        value={sphere}
        onChange={(k) => {
          setParam('sphere', k);
          if (picked && k && picked.sphereKey !== k) setParam('project', null);
        }}
      />
      <div className="tk-bar">
        <label className="tk-proj">
          <select aria-label="Project" value={project ?? ''} onChange={(e) => setParam('project', e.target.value || null)}>
            <option value="">All projects</option>
            {inJob.map(({ project: p, depth }) => (
              <option key={p.id} value={p.id}>
                {`${'\u00a0\u00a0'.repeat(depth)}${depth > 0 ? '↳ ' : ''}${p.name}`}
              </option>
            ))}
          </select>
        </label>
        {sortCount > 0 && (
          <Chip to={`/tasks?mode=list&view=to_sort${sphere ? `&sphere=${encodeURIComponent(sphere)}` : ''}`} count={sortCount} label={`To sort, ${sortCount}`}>
            To sort
          </Chip>
        )}
        <div className="tk-mode" role="group" aria-label="Show as">
          <button type="button" aria-pressed={mode === 'board'} className={mode === 'board' ? 'tk-mode--on' : ''} onClick={() => setMode('board')}>
            <Icon name="grid" size={16} /> Board
          </button>
          <button type="button" aria-pressed={mode === 'list'} className={mode === 'list' ? 'tk-mode--on' : ''} onClick={() => setMode('list')}>
            <Icon name="list" size={16} /> List
          </button>
        </div>
      </div>
      {mode === 'board' ? (
        <Board
          key={boardKey}
          sphere={sphere}
          project={project}
          projectSphere={picked?.sphereKey}
          lanes={sphere ? 'project' : 'sphere'}
          projects={projects}
          onChanged={() => void toSort.reload()}
        />
      ) : (
        <TaskLists sphere={sphere} project={project} projects={projects} sortCount={sortCount} onSorted={() => void toSort.reload()} />
      )}
    </Page>
  );
}

/** The list view's header line ("Today · 2 tasks"), read from the same URL. */
function ListSub({ sphere }: { sphere: string | null }): JSX.Element | null {
  const [params] = useParamSetter();
  const meta = TASK_TABS.find((t) => t.value === tabFromParam(params.get('view')))!;
  const q = params.get('q') ?? '';
  const tasks = useLoad(() => listTasks({ view: meta.server, sphere: sphere ?? undefined, q: q || undefined }), [meta.server, sphere, q]);
  return tasks.data ? <>{`${meta.label} · ${plural(tasks.data.total, 'task')}`}</> : null;
}

/** The older Things-style lists: Today · Upcoming · Someday · Done (+ To sort). */
function TaskLists({
  sphere,
  project,
  projects,
  sortCount,
  onSorted,
}: {
  sphere: string | null;
  project: string | null;
  projects: readonly Project[];
  sortCount: number;
  onSorted: () => void;
}): JSX.Element {
  const [params, setParam] = useParamSetter();
  const tab = tabFromParam(params.get('view'));
  const meta = TASK_TABS.find((t) => t.value === tab)!;
  const q = params.get('q') ?? '';
  const spheres = useSpheres();
  const today = ymdInHomeZone(new Date());

  const tasks = useLoad(() => listTasks({ view: meta.server, sphere: sphere ?? undefined, q: q || undefined }), [meta.server, sphere, q]);
  // Waiting is a group inside Today and Upcoming, not a view of its own.
  const showsWaiting = (tab === 'today' || tab === 'upcoming') && !q;
  const waiting = useLoad<{ items: TaskRow[]; total: number } | null>(
    () => (showsWaiting ? listTasks({ view: 'waiting', sphere: sphere ?? undefined }) : Promise.resolve(null)),
    [showsWaiting, sphere],
  );

  const reloadAll = async (): Promise<void> => {
    onSorted();
    await Promise.all([tasks.reload(), waiting.reload()]);
  };
  const actions = useTaskActions(reloadAll);
  useCaptured(() => void reloadAll());
  const [menu, setMenu] = useState<TaskRow | null>(null);
  const [sorting, setSorting] = useState<TaskRow | null>(null);
  const [editing, setEditing] = useState<TaskRow | null>(null);

  const tabs = TASK_TABS.filter((t) => t.value !== 'to_sort' || sortCount > 0 || tab === 'to_sort').map((t) =>
    t.value === 'to_sort' ? { ...t, count: sortCount } : t,
  );

  const row = (t: TaskRow): JSX.Element => {
    const color = sphereColor(spheres, t.sphereKey);
    if (tab === 'done') {
      return <TaskItem key={t.id} task={t} today={today} color={color} done onToggle={() => actions.reopen(t)} />;
    }
    if (tab === 'to_sort') {
      return (
        <TaskItem
          key={t.id}
          task={t}
          today={today}
          color={color}
          onToggle={() => actions.complete(t)}
          action={{ label: 'Sort', onClick: () => setSorting(t) }}
        />
      );
    }
    return (
      <TaskItem
        key={t.id}
        task={t}
        today={today}
        color={color}
        onToggle={() => actions.complete(t)}
        onSnooze={() => actions.snooze(t, 'tomorrow')}
        onMenu={() => setMenu(t)}
      />
    );
  };

  // The project filter narrows the lists too (the project and its sub-projects).
  const inProject = useMemoProjectIds(projects, project);
  const visible = (rows: readonly TaskRow[]): TaskRow[] =>
    rows.filter((t) => !actions.hidden.has(t.id) && (!inProject || (t.projectId !== null && inProject.has(t.projectId))));
  const waitingRows = visible(waiting.data?.items ?? []);

  return (
    <>
      {q && (
        <div className="ui-chips">
          <Chip on onClick={() => setParam('q', null)} label={`Clear search ${q}`}>
            Matching “{q}”
            <Icon name="x" size={14} />
          </Chip>
        </div>
      )}
      <Segmented label="Task view" value={tab} options={tabs} onChange={(v) => setParam('view', v === 'today' ? null : v)} />

      <Loader state={tasks}>
        {(d) => {
          const rows = visible(d.items);
          // The server's views can already carry waiting tasks; never show one twice.
          const ids = new Set(rows.map((t) => t.id));
          const extraWaiting = waitingRows.filter((t) => !ids.has(t.id));
          const body =
            tab === 'today' ? (
              <TodayList rows={rows} projects={projects} render={row} />
            ) : tab === 'upcoming' ? (
              <UpcomingList rows={rows} waiting={extraWaiting} today={today} render={row} />
            ) : (
              <ProjectGroups label={meta.label} rows={rows} projects={projects} render={row} />
            );
          const todayWaiting =
            tab === 'today' ? extraWaiting.filter((t) => t.startOn === null || t.startOn <= today) : [];
          const laterWaiting = tab === 'upcoming' ? extraWaiting.filter((t) => t.startOn !== null && t.startOn > today) : [];
          const empty = rows.length === 0 && todayWaiting.length === 0 && laterWaiting.length === 0;
          return (
            <>
              {empty ? <EmptyState icon={meta.icon} title={meta.empty} hint={meta.hint} /> : body}
              {todayWaiting.length > 0 && (
                <Group header="Waiting on others" id="tasks-waiting">
                  {todayWaiting.map(row)}
                </Group>
              )}
              {d.total > d.items.length && (
                <p className="ui-foot">
                  Showing {d.items.length} of {d.total}.
                </p>
              )}
            </>
          );
        }}
      </Loader>

      {menu && <TaskActionsSheet task={menu} actions={actions} onEdit={() => setEditing(menu)} onClose={() => setMenu(null)} />}
      {sorting && <SortSheet task={sorting} today={today} actions={actions} onClose={() => setSorting(null)} />}
      {editing && <TaskEditSheet task={editing} onClose={() => setEditing(null)} onSaved={() => void reloadAll()} />}
    </>
  );
}

/** The ids of a project and everything under it, or null for no filter. */
function useMemoProjectIds(projects: readonly Project[], project: string | null): ReadonlySet<string> | null {
  return useMemo(() => {
    if (!project) return null;
    const ids = new Set([project]);
    for (let grew = true; grew; ) {
      grew = false;
      for (const p of projects) {
        if (p.parentId && ids.has(p.parentId) && !ids.has(p.id)) {
          ids.add(p.id);
          grew = true;
        }
      }
    }
    return ids;
  }, [projects, project]);
}

/**
 * Rows split by project, sub-projects indented under their parent. With no
 * projects at all it is one plain group labelled `label`, as before.
 */
export function ProjectGroups({
  label,
  rows,
  projects,
  render,
  more,
}: {
  label: string;
  rows: readonly TaskRow[];
  projects: readonly Project[];
  render: (t: TaskRow) => JSX.Element;
  /** A trailing "Show n more" row, inside the last group. */
  more?: ReactNode;
}): JSX.Element | null {
  if (rows.length === 0) return null;
  const groups = groupByProject(rows, projects);
  if (groups.length === 1 && groups[0]!.projectId === null) {
    return (
      <Group label={label}>
        {rows.map(render)}
        {more}
      </Group>
    );
  }
  return (
    <div className="tasks__projects" role="region" aria-label={label}>
      {groups.map((g, i) => (
        <Group
          key={g.projectId ?? 'none'}
          header={g.name}
          className={`tasks__proj${g.depth > 0 ? ' tasks__proj--sub' : ''}${g.projectId === null ? ' tasks__proj--none' : ''}`}
        >
          {g.rows.map(render)}
          {i === groups.length - 1 && more}
        </Group>
      ))}
    </div>
  );
}

function TodayList({
  rows,
  projects,
  render,
}: {
  rows: TaskRow[];
  projects: readonly Project[];
  render: (t: TaskRow) => JSX.Element;
}): JSX.Element | null {
  const [all, setAll] = useState(false);
  if (rows.length === 0) return null;
  // The cap applies to the ranked list first; the shown rows are then grouped.
  const shown = all ? rows : rows.slice(0, TODAY_SHOWN);
  return (
    <ProjectGroups
      label="Today"
      rows={shown}
      projects={projects}
      render={render}
      more={<MoreRow count={rows.length - shown.length} onClick={() => setAll(true)} />}
    />
  );
}

/** Day-grouped: tasks by due date, waiting tasks under their (future) chase date. */
function UpcomingList({
  rows,
  waiting,
  today,
  render,
}: {
  rows: TaskRow[];
  waiting: TaskRow[];
  today: string;
  render: (t: TaskRow) => JSX.Element;
}): JSX.Element {
  const days = new Map<string, TaskRow[]>();
  const later: TaskRow[] = [];
  const add = (key: string | null, t: TaskRow): void => {
    if (!key) {
      later.push(t);
      return;
    }
    days.set(key, [...(days.get(key) ?? []), t]);
  };
  for (const t of rows) add(t.dueOn ?? t.startOn, t);
  for (const t of waiting) if (t.startOn && t.startOn > today) add(t.startOn, t);
  const groups: ReactNode[] = [...days.keys()].sort().map((ymd) => (
    <Group key={ymd} header={dayHeader(ymd, today)} className="tasks__day">
      {days.get(ymd)!.map(render)}
    </Group>
  ));
  return (
    <>
      {groups}
      {later.length > 0 && <Group header="Later">{later.map(render)}</Group>}
    </>
  );
}
